<template>
  <div class="patient-measurement-results">
    <div class="area buttons">
      <div v-if="!printFlg" class="wrapper" data-test="button-wrapper default">
        <base-button-register
          data-test="base-button-register idexx"
          :text="'IDEXX 取込'"
          @click="openIdexxPopup"
        />
        <base-button-register
          data-test="base-button-register unlinked"
          :text="'検査結果取込'"
          @click="openUnlinkedPopup"
        />
        <base-button-border-orange
          data-test="base-button-border-orange default"
          :styles="{ width: '120px' }"
          @click="onPrintMode"
          >検査結果印刷</base-button-border-orange
        >
      </div>
      <div v-if="printFlg" class="wrapper" data-test="button-wrapper print">
        <base-button-small-white
          data-test="base-button-small-white cancel"
          :styles="{ width: '120px' }"
          @click="offPrintMode"
          >キャンセル</base-button-small-white
        >
        <base-button-border-orange
          data-test="base-button-border-orange print"
          :styles="{ width: '120px' }"
          :disabled="!canPrint"
          @click="openPrintPopup"
          >印刷</base-button-border-orange
        >
      </div>
    </div>
    <div class="area owner-patient">
      <owner-patient-detail
        data-test="owner-patient-detail"
        :owner="owner"
        :patient="patient"
      />
    </div>
    <div class="area search">
      <search-area
        data-test="search-area"
        :period-flg="true"
        :period-label="'期間'"
        :placeholder="'検査名、検査機器'"
        :text-box-label="'検索単語'"
        :text-value="searchText"
        @clear-end-date="inputDate('searchEndDate', '')"
        @clear-start-date="inputDate('searchStartDate', '')"
        @clear="clearDate"
        @input-end-date="inputDate('searchEndDate', $event)"
        @input-start-date="inputDate('searchStartDate', $event)"
        @input="inputText"
      />
    </div>
    <div class="area search-result">
      <span class="label">検索結果：</span>
      <span class="counts">{{ searchCountsText }}</span>
    </div>
    <div class="area list">
      <measurement-result-list-table
        v-if="filteredMeasurementResults.length > 0"
        data-test="measurement-result-list-table"
        :checkbox-description="checkboxDescription"
        :checkbox-statuses="checkboxStatuses"
        :is-check-limit="isCheckLimit"
        :measurement-results="filteredMeasurementResults"
        :use-checkbox="printFlg"
        @check="checkItem"
        @click="openDetailPopup"
      />
    </div>
    <base-loading data-test="base-loading" :wait-flg="isGettingData" />
    <idexx-measurement-result-popup
      v-if="showIdexxPopup"
      data-test="idexx-measurement-result-popup"
      :patient-id="patientId"
      @close="closeIdexxPopup"
    />
    <measurement-result-popup
      v-if="showDetailPopup"
      data-test="measurement-result-popup"
      :buttons="['閉じる', '取込解除']"
      :can-delete="true"
      :can-update="true"
      :measurement-result-id="selectedMeasurementResultId"
      :patient-id="patientId"
      @close="closeDetailPopup"
    />
    <print-measurement-results-popup
      v-if="showPrintPopup"
      data-test="print-measurement-results-popup"
      :measurement-results="printMeasurementResults"
      :patient="patient"
      :owner="owner"
      @close="closePrintPopup"
    />
    <unlinked-measurement-results-popup
      v-if="showUnlinkedPopup"
      data-test="unlinked-measurement-results-popup"
      :patient-id="patientId"
      @close="closeUnlinkedPopup"
    />
  </div>
</template>

<script>
import BaseButtonBorderOrange from '@/components/parts/atoms/BaseButtonBorderOrange'
import BaseButtonRegister from '@/components/parts/atoms/BaseButtonRegister'
import BaseButtonSmallWhite from '@/components/parts/atoms/BaseButtonSmallWhite'
import BaseLoading from '@/components/parts/atoms/BaseLoading'
import IdexxMeasurementResultPopup from '@/components/popups/IdexxMeasurementResultPopup'
import MeasurementResultListTable from '@/components/parts/organisms/MeasurementResultListTable'
import MeasurementResultPopup from '@/components/popups/MeasurementResultPopup'
import OwnerPatientDetail from '@/components/parts/molecules/OwnerPatientDetail'
import PrintMeasurementResultsPopup from '@/components/popups/PrintMeasurementResultsPopup'
import SearchArea from '@/components/parts/molecules/SearchArea'
import UnlinkedMeasurementResultsPopup from '@/components/popups/UnlinkedMeasurementResultsPopup'
import { compareByKeys } from '@/utils/compare'
import { mapGetters } from 'vuex'

export default {
  name: 'PatientMeasurementResults',

  components: {
    BaseButtonBorderOrange,
    BaseButtonRegister,
    BaseButtonSmallWhite,
    BaseLoading,
    IdexxMeasurementResultPopup,
    MeasurementResultListTable,
    MeasurementResultPopup,
    OwnerPatientDetail,
    PrintMeasurementResultsPopup,
    SearchArea,
    UnlinkedMeasurementResultsPopup
  },

  props: {
    ownerId: { type: Number },
    patientId: { type: Number }
  },

  data() {
    return {
      checkboxStatuses: [],
      isGettingData: false,
      maxPrintCounts: 3,
      printFlg: false,
      printMeasurementResults: [],
      searchEndDate: '',
      searchStartDate: '',
      searchText: '',
      selectedMeasurementResultId: 0,
      showDetailPopup: false,
      showIdexxPopup: false,
      showPrintPopup: false,
      showUnlinkedPopup: false
    }
  },

  computed: {
    ...mapGetters({
      getMasterLaboratoryCategory:
        'master/getMasterLaboratoryCategoryIndexedByCode',
      getMasterLaboratoryInstrument:
        'master/getMasterLaboratoryInstrumentIndexedByCode',
      getMeasurementResults: 'measurementResults/getDataByPatientId',
      getOwner: 'owners/getDataById',
      getPatient: 'patients/getDataById'
    }),
    canPrint() {
      return 1 <= this.checkedCounts
    },
    checkboxDescription() {
      const counts = this.maxPrintCounts
      return `同時に印刷できる検査結果は ${counts}件 までです`
    },
    checkedCounts() {
      return this.checkboxStatuses.filter(v => v.isChecked).length
    },
    filteredMeasurementResults() {
      return this.patientMeasurementResults
        .filter(v => this.filterByDate(v) && this.filterByText(v))
        .sort((a, b) => compareByKeys(a, b, ['date', 'time', 'id']))
    },
    isCheckLimit() {
      return this.maxPrintCounts <= this.checkedCounts
    },
    owner() {
      return this.getOwner(this.ownerId)
    },
    patient() {
      return this.getPatient(this.patientId)
    },
    patientMeasurementResults() {
      const patientMeasurementResults =
        this.getMeasurementResults(this.patientId) ?? []
      return patientMeasurementResults.map(v => {
        const categoryCode = v.masterLaboratoryCategoryCode
        const instrumentCode = v.masterLaboratoryInstrumentCode
        const category = this.getMasterLaboratoryCategory(categoryCode)
        const instrument = this.getMasterLaboratoryInstrument(instrumentCode)
        return { ...v, category, instrument }
      })
    },
    searchCountsText() {
      const allCounts = this.patientMeasurementResults.length
      const filteredCounts = this.filteredMeasurementResults.length
      return `${filteredCounts}件（${allCounts}件中）`
    },
    trimmedSearchText() {
      return this.searchText.toLowerCase().trim()
    }
  },

  watch: {
    filteredMeasurementResults: function() {
      if (this.printFlg) this.setCheckboxStatuses()
    }
  },

  methods: {
    checkItem({ isChecked, measurementResultId }) {
      const target = this.checkboxStatuses.find(
        checkboxStatus =>
          checkboxStatus.measurementResultId === measurementResultId
      )
      target.isChecked = isChecked
    },
    clearDate() {
      this.searchStartDate = ''
      this.searchEndDate = ''
    },
    closeDetailPopup() {
      this.showDetailPopup = false
      this.selectedMeasurementResultId = 0
    },
    closeIdexxPopup() {
      this.showIdexxPopup = false
    },
    closePrintPopup() {
      this.showPrintPopup = false
      this.printMeasurementResults = []
    },
    closeUnlinkedPopup() {
      this.showUnlinkedPopup = false
    },
    async getUnlinkedData() {
      this.isGettingData = true
      const result = await this.$store.dispatch(
        'measurementResults/getUnlinkedData'
      )
      if (result !== 'success') {
        this.$store.dispatch('petorelu/showGlobalPopup', {
          show: true,
          type: 'failure',
          title: '失敗',
          buttons: ['閉じる'],
          message: '検査データの取得に失敗しました。',
          close: () => this.$store.dispatch('petorelu/hideGlobalPopup')
        })
      }
      this.isGettingData = false
      return result
    },
    filterByDate(measurementResult) {
      const date = measurementResult.date
      const startDate = this.searchStartDate
      const endDate = this.searchEndDate
      if (startDate !== '' && endDate !== '') {
        return startDate <= date && date <= endDate
      } else if (startDate !== '') {
        return startDate <= date
      } else if (endDate !== '') {
        return date <= endDate
      } else {
        return true
      }
    },
    filterByText(measurementResult) {
      const { category, instrument } = measurementResult
      return (
        this.matchesSearchText(category.name) ||
        this.matchesSearchText(instrument.name)
      )
    },
    inputDate(key, date) {
      this[key] = date
    },
    inputText(text) {
      this.searchText = text
    },
    matchesSearchText(itemText) {
      return itemText.toLowerCase().includes(this.trimmedSearchText)
    },
    offPrintMode() {
      this.printFlg = false
      this.checkboxStatuses = []
    },
    onPrintMode() {
      this.printFlg = true
      this.setCheckboxStatuses()
    },
    openDetailPopup(measurementResult) {
      this.selectedMeasurementResultId = measurementResult.id
      this.showDetailPopup = true
    },
    openIdexxPopup() {
      this.showIdexxPopup = true
    },
    openPrintPopup() {
      this.printMeasurementResults = this.filteredMeasurementResults.filter(
        measurementResult => {
          const target = this.checkboxStatuses.find(
            checkboxStatus =>
              checkboxStatus.measurementResultId === measurementResult.id
          )
          return target?.isChecked
        }
      )
      this.showPrintPopup = true
    },
    async openUnlinkedPopup() {
      const result = await this.getUnlinkedData()
      if (result === 'success') {
        this.showUnlinkedPopup = true
      }
    },
    setCheckboxStatuses() {
      this.checkboxStatuses = this.filteredMeasurementResults.map(
        measurementResult => {
          const existingCheckboxStatus = this.checkboxStatuses.find(
            checkboxStatus =>
              checkboxStatus.measurementResultId === measurementResult.id
          )
          return {
            measurementResultId: measurementResult.id,
            isChecked: existingCheckboxStatus?.isChecked ? true : false
          }
        }
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.patient-measurement-results {
  display: inline-block;
  min-width: 100%;
  > .buttons {
    > .wrapper {
      display: flex;
      justify-content: flex-end;
      gap: 0 20px;
    }
  }
  > .owner-patient {
    margin-top: 20px;
  }
  > .search {
    margin-top: 40px;
  }
  > .search-result {
    margin-top: 30px;
    height: 33px;
    display: flex;
    align-items: center;
    font-size: 13px;
    > .counts {
      font-feature-settings: 'palt';
    }
  }
  > .list {
    margin-top: 15px;
    ::v-deep .table-body {
      max-height: 630px;
    }
  }
}
</style>
